import { useCallback, useMemo, useRef } from 'react';
import { createInfiniteQuery } from 'react-query-kit';
import { keepPreviousData } from '@tanstack/react-query';

import {
  type QuizBankCursorRes,
  type QuizBankSearchReq,
  searchQuizBankItems,
} from '@/api/quizBank';
import { matchesApiErrorStatus } from '@/shared/error/isZepQuizApiError';

import { type QuizBankFilterState } from '../types';
import { toQuizBankSearchReq } from '../utils';

interface UseQuizBankSearchPagesParams {
  /** 7자리 카테고리 트리 id. 없으면 조회하지 않는다 */
  categoryTreeId?: string;
  filter: QuizBankFilterState;
  /** 페이지당 개수 (서버 상한 50) */
  limit?: number;
}

const DEFAULT_LIMIT = 20;

/** 서명·형식·payload 버전·TTL(30분)·필터 해시 검증 실패를 묶은 400 (`QuizBankCursorCodec.decode`). */
const INVALID_CURSOR_STATUS = 'QUIZ_BANK_INVALID_CURSOR';

/**
 * "0건 + hasNext" 페이지를 이어 받는 횟수 상한 (`useGetAllQuizes` 와 동일).
 * 서버가 비공개 전환된 문항을 조회 후에 떨어뜨려 빈 페이지에 커서를 실어 보낼 수
 * 있는데, 그대로 올리면 목록이 자라지 않아 스크롤 트리거가 다시 나지 않는다.
 */
const MAX_EMPTY_CURSOR_CONTINUATIONS = 3;

/**
 * 빈 페이지면 커서로 이어 받아 비지 않은 페이지를 돌려준다.
 * `facets`·`totalCount` 는 첫 응답에만 있으므로 이어 받은 응답에 되살린다.
 */
const searchSkippingEmptyContinuations = async (
  req: QuizBankSearchReq,
): Promise<QuizBankCursorRes> => {
  const first = await searchQuizBankItems(req);
  let response = first;

  for (
    let skipped = 0;
    skipped < MAX_EMPTY_CURSOR_CONTINUATIONS;
    skipped += 1
  ) {
    if (response.data.items.length > 0 || !response.hasNext) break;
    if (!response.nextCursor) break;

    response = await searchQuizBankItems({
      ...req,
      cursor: response.nextCursor,
    });
  }

  if (response === first) return first;
  return {
    ...response,
    data: {
      ...response.data,
      facets: response.data.facets ?? first.data.facets,
    },
    totalCount: response.totalCount ?? first.totalCount,
  };
};

interface QuizBankSearchVariables {
  categoryTreeId: string;
  filter: QuizBankFilterState;
  limit: number;
}

/**
 * 커서는 필터 조합에 묶여 있어 필터 변경 후 재사용하면 400 이다. `variables` 가
 * queryKey 에 들어가므로 필터가 바뀌면 캐시 엔트리가 갈리고 커서 없이 다시 시작한다.
 */
const useQuizBankSearchQuery = createInfiniteQuery<
  QuizBankCursorRes,
  QuizBankSearchVariables,
  Error,
  string | undefined
>({
  queryKey: ['quiz-bank/items/search'],
  fetcher: ({ categoryTreeId, filter, limit }, { pageParam }) =>
    searchSkippingEmptyContinuations(
      toQuizBankSearchReq({ categoryTreeId, filter, limit, cursor: pageParam }),
    ),
  getNextPageParam: lastPage =>
    lastPage.hasNext ? lastPage.nextCursor ?? undefined : undefined,
  initialPageParam: undefined,
  /**
   * 앱 기본값 `throwOnError: true`(`_app.page.tsx`)를 상속하면 실패가 바운더리로 먼저 throw 돼
   * 모달 안 에러·재시도 UI 가 렌더될 기회를 잃는다. 보고는 `queryCache.onError` 가 계속 한다.
   */
  throwOnError: false,
  // 필터를 바꾸는 동안 이전 facets 를 유지해 좌측 패널이 사라졌다 돌아오지 않게 한다.
  placeholderData: keepPreviousData,
  // 무한 쿼리 refetch 는 쌓인 페이지를 전부 다시 가져온다.
  refetchOnWindowFocus: false,
});

/**
 * 커서 거절 래치 — 거절을 본 순간 서고 조회가 성공해야 풀린다. 복구 refetch 가 5xx 로
 * 실패해도 유지돼야 다음 재시도가 캐시에 남은 거절된 커서로 돌아가지 않는다.
 */
const useCursorResetLatch = (rejected: boolean, resolved: boolean) => {
  const latched = useRef(false);
  latched.current = rejected || (latched.current && !resolved);
  return latched.current;
};

/** 더 보기 실패는 목록을 지켜야 하므로 success 를 유지한다. placeholder 는 이전 필터 결과다. */
const toListStatus = ({
  isError,
  isNextPageError,
  isPending,
  isPlaceholderData,
}: {
  isError: boolean;
  isNextPageError: boolean;
  isPending: boolean;
  isPlaceholderData: boolean;
}) => {
  if (isError && !isNextPageError) return 'error' as const;
  if (isPending || isPlaceholderData) return 'loading' as const;
  return 'success' as const;
};

export const useQuizBankSearchPages = ({
  categoryTreeId,
  filter,
  limit = DEFAULT_LIMIT,
}: UseQuizBankSearchPagesParams) => {
  const query = useQuizBankSearchQuery({
    variables: { categoryTreeId: categoryTreeId ?? '', filter, limit },
    enabled: Boolean(categoryTreeId),
  });

  const items = useMemo(
    () => query.data?.pages.flatMap(page => page.data.items ?? []) ?? [],
    [query.data],
  );

  // facets·totalCount 는 첫 페이지에만 실려 온다.
  const firstPage = query.data?.pages[0];

  /**
   * 다음 페이지 실패는 목록과 선택 맥락을 지킨 채 하단에서만 받는다. 5.52 에는
   * `isFetchNextPageError` 가 없어 "이 필터로 직접 받아 둔 페이지가 있는데 에러"로 판별한다 —
   * 첫 조회 실패는 placeholder(이전 필터 결과)만 들고 있으므로 걸리지 않는다.
   */
  const isNextPageError =
    query.isError &&
    !query.isPlaceholderData &&
    (query.data?.pages.length ?? 0) > 0;

  /** 거절된 커서는 사유가 무엇이든(TTL·필터 해시·payload 버전) 재전송하면 같은 400 이다. */
  const shouldRecoverCursor = useCursorResetLatch(
    isNextPageError &&
      matchesApiErrorStatus(query.error, INVALID_CURSOR_STATUS),
    !query.isError,
  );

  const { fetchNextPage: fetchNext, refetch } = query;

  /**
   * 거절이면 이어 받지 않고 처음부터 새 커서로 다시 쌓는다. `refetch` 는 첫 페이지만 커서 없이
   * 받고 이후 파라미터를 새 응답에서 재계산하므로(query-core 5.52 `infiniteQueryBehavior`)
   * 쌓아 둔 깊이를 그대로 복구한다.
   */
  const fetchNextPage = useCallback(
    () => (shouldRecoverCursor ? refetch() : fetchNext()),
    [shouldRecoverCursor, refetch, fetchNext],
  );

  return {
    items,
    facets: firstPage?.data.facets ?? null,
    totalCount: firstPage?.totalCount ?? undefined,
    status: toListStatus({
      isError: query.isError,
      isNextPageError,
      isPending: query.isPending,
      isPlaceholderData: query.isPlaceholderData,
    }),
    hasNextPage: query.hasNextPage,
    // 커서 복구도 하단 스켈레톤으로 보인다 — 재시도가 먹었는지 화면으로 알 수 있어야 한다.
    isFetchingNextPage:
      query.isFetchingNextPage || (shouldRecoverCursor && query.isRefetching),
    /** 목록은 그대로 두고 하단에서만 재시도를 받는다 */
    isNextPageError,
    fetchNextPage,
    refetch,
  };
};
